// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionAckAlarm } from "actions";
import { CloudSettings } from "shared/types/cloudtypes";
import { AlarmSource, AlarmState, IAlarmEntry } from "shared/types/globtypes";
import { IParameter } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import AlarmModel from "model/alarm";

import FormattedDate from "./FormattedDate";
import FormattedTime from "./FormattedTime";

interface IAlarmEntryExtProps {
  index: number;
  isEditable: boolean;
  filterText?: string;
}

type IAlarmEntryProps = IAlarmEntry & {
  parameter: IParameter;
  cloudSettings: CloudSettings | undefined;
};

const mapStateToProps = (state: IState, ownProps: IAlarmEntryExtProps) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const props: IAlarmEntryProps = {
    ...state.data.alarms[ownProps.index],
    parameter: state.para.parameter!,
    cloudSettings: site?.cloudSettings,
  };
  return props;
};

interface IAlarmEntryDispatch {
  onAlarmAck: (alarmTimestamp: number, source: AlarmSource, value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmEntryDispatch = {
    onAlarmAck: (alarmTimestamp: number, source: AlarmSource, value: number) => {
      const payload: IActionAckAlarm = {
        alarmTimestamp,
        source,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_ACK_ALARM_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  ackTooltip: {
    id: "acknowledge this alarm and older alarms of same type",
  },
});

class AlarmEntryComponent extends React.Component<
  IAlarmEntryProps & IAlarmEntryExtProps & IAlarmEntryDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const alarmModel = new AlarmModel(this.props, this.props.parameter, this.props.cloudSettings);

    if (
      !isSet(this.props.filterText) ||
      alarmModel
        .getText()
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase()) ||
      this.context
        .formatMessage({ id: `AlarmSource_${alarmModel.data.source}` })
        .toLocaleLowerCase()
        .includes(this.props.filterText.toLocaleLowerCase())
    ) {
      return (
        <tr className="alarmentryitem">
          <td className="center">
            <FormattedDate valueS={this.props.alarmTimestamp} />{" "}
            <FormattedTime valueS={this.props.alarmTimestamp} />
          </td>
          <td>
            <FormattedMessage id={`AlarmSource_${this.props.source}`} />
          </td>
          <td>{this.getStateText()}</td>
          <td className="optional-sm">{alarmModel.getText()}</td>
          {this.props.isEditable && (
            <td className="alarmAck">
              {![AlarmState.Ackd, AlarmState.AckdActive].includes(this.props.state) && (
                <Button
                  style={{ marginBottom: 2 }}
                  id="graphforward"
                  variant="secondary"
                  className="me-1"
                  title={this.context.formatMessage(messages.ackTooltip)}
                  onClick={(event) =>
                    this.props.onAlarmAck(
                      this.props.alarmTimestamp,
                      this.props.source,
                      this.props.value,
                    )
                  }
                >
                  <FormattedMessage id="Acknowledge" />
                </Button>
              )}
            </td>
          )}
        </tr>
      );
    }
  }

  private getStateText() {
    if (this.props.state === AlarmState.Active) {
      return <FormattedMessage id="active" />;
    }
    if (this.props.state === AlarmState.AckPending) {
      return (
        <span
          title={
            isSet(this.props.clearTimestamp) ?
              new Date(this.props.clearTimestamp * 1000).toLocaleString()
            : undefined
          }
        >
          <FormattedMessage id="cleared" />
        </span>
      );
    }
    if (this.props.state === AlarmState.AckdActive) {
      return <FormattedMessage id="active, acknowledged" />;
    }
    // (this.props.state === AlarmState.Ackd)
    return <FormattedMessage id="acknowledged" />;
  }
}

const AlarmEntry = connect(mapStateToProps, mapDispatchToProps)(AlarmEntryComponent);

export default AlarmEntry;
